import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Info, Plus, Trash2 } from 'lucide-react';
import type {
  BackofficeMerchantAccessProfileOptionDto,
  BackofficeMerchantEmployeeDto,
  BackofficeMerchantEmployeeRequestDto,
  MerchantRole,
} from '@kardume/types';
import { MERCHANT_ROLE_LABELS } from '@kardume/types';
import ActionButton from './ActionButton';
import Button from './Button';
import DocumentInput from './DocumentInput';
import PhoneInput from './PhoneInput';
import Select from './Select';
import Switch from './Switch';
import TextInput from './TextInput';

/**
 * Um funcionário em edição.
 *
 * É um tipo próprio, e não o `...RequestDto`, porque campo de formulário não
 * tem `undefined`: `accessProfileId` vazio é string vazia enquanto o operador
 * digita e só vira `null` no payload. `merchantEmployeeToPayload` faz a ponte.
 */
export interface MerchantEmployeeFormValue {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  document: string;
  role: MerchantRole;
  accessEnabled: boolean;
  accessProfileId: string;
}

/** Mensagens de uma linha, na chave do campo a que pertencem. */
export interface MerchantEmployeeFieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  document?: string;
  accessProfileId?: string;
}

export const emptyMerchantEmployee = (role: MerchantRole = 'STAFF'): MerchantEmployeeFormValue => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  document: '',
  role,
  accessEnabled: true,
  accessProfileId: '',
});

/** Reabre no formulário o que a API devolveu. */
export const merchantEmployeeFromDto = (
  employee: BackofficeMerchantEmployeeDto
): MerchantEmployeeFormValue => ({
  firstName: employee.firstName,
  lastName: employee.lastName,
  email: employee.email,
  phone: employee.phone ?? '',
  document: employee.document ?? '',
  role: employee.role,
  // Cadastro antigo pode não trazer o campo; nesse caso o acesso segue o status.
  accessEnabled: employee.accessEnabled ?? employee.status === 'ACTIVE',
  accessProfileId: employee.accessProfileId ?? '',
});

/** Uma linha que o operador abriu e nunca tocou - não é funcionário, é espaço em branco. */
export const isBlankMerchantEmployee = (employee: MerchantEmployeeFormValue): boolean =>
  !employee.firstName.trim() &&
  !employee.lastName.trim() &&
  !employee.email.trim() &&
  !employee.phone.trim() &&
  !employee.document.trim();

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ValidateMerchantEmployeeOptions {
  /**
   * E-mails já ocupados que o cliente consegue conhecer (as outras linhas da
   * lista, os funcionários já cadastrados da loja). A unicidade de verdade é
   * GLOBAL e só o core a garante - isto aqui pega o caso comum antes do request.
   */
  takenEmails?: string[];
}

/**
 * Espelha as regras do core para o erro aparecer antes do request.
 *
 * Nunca mais rígida que o backend: perfil de acesso só é cobrado de STAFF com
 * acesso liberado (`ACCESS_PROFILE_REQUIRED`), porque OWNER é super-usuário da
 * própria loja e dispensa perfil.
 */
export const validateMerchantEmployee = (
  employee: MerchantEmployeeFormValue,
  options: ValidateMerchantEmployeeOptions = {}
): MerchantEmployeeFieldErrors => {
  const errors: MerchantEmployeeFieldErrors = {};
  if (isBlankMerchantEmployee(employee)) return errors;

  if (!employee.firstName.trim()) errors.firstName = 'Informe o nome.';
  if (!employee.lastName.trim()) errors.lastName = 'Informe o sobrenome.';

  const email = employee.email.trim().toLowerCase();
  if (!email) errors.email = 'Informe o e-mail.';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'E-mail inválido.';
  else if ((options.takenEmails ?? []).some((taken) => taken.trim().toLowerCase() === email)) {
    errors.email = 'Este e-mail já está em uso.';
  }

  // Opcionais desde o card 241, e opcional NÃO é "sem validação": ausente passa,
  // pela metade não. Quem cadastra pelo backoffice monta o guia da cidade a
  // partir de informação pública e raramente tem o CPF do responsável - exigir
  // os dois só produzia `000.000.000-00` digitado para destravar o botão.
  const phone = employee.phone.replace(/\D/g, '');
  if (phone && (phone.length < 10 || phone.length > 11)) errors.phone = 'Telefone incompleto.';

  const document = employee.document.replace(/\D/g, '');
  if (document && document.length !== 11) errors.document = 'CPF incompleto.';

  if (employee.role === 'STAFF' && employee.accessEnabled && !employee.accessProfileId) {
    errors.accessProfileId = 'Funcionário com acesso liberado precisa de um perfil.';
  }

  return errors;
};

export const hasMerchantEmployeeErrors = (errors: MerchantEmployeeFieldErrors): boolean =>
  Object.values(errors).some(Boolean);

/**
 * A primeira linha já foi obrigatória: era de onde o payload tirava o
 * `responsible` da loja, e por isso `requireFirst` existia. O card 241 removeu
 * essa exigência - a equipe inteira pode ficar vazia, e quem monta o payload
 * manda `responsible` AUSENTE em vez de um objeto de campos em branco. A opção
 * saiu junto para não sobrar um interruptor que ninguém liga.
 */
export type ValidateMerchantTeamOptions = ValidateMerchantEmployeeOptions;

/** Valida a lista inteira, incluindo o e-mail repetido entre duas linhas. */
export const validateMerchantTeam = (
  team: MerchantEmployeeFormValue[],
  options: ValidateMerchantTeamOptions = {}
): MerchantEmployeeFieldErrors[] =>
  team.map((employee, index) =>
    validateMerchantEmployee(employee, {
      takenEmails: [
        ...(options.takenEmails ?? []),
        // Só as linhas ANTERIORES: assim a duplicidade é apontada na segunda
        // ocorrência, e não nas duas - que deixaria o operador sem saber qual apagar.
        ...team.slice(0, index).map((other) => other.email),
      ],
    })
  );

export const hasMerchantTeamErrors = (errors: MerchantEmployeeFieldErrors[]): boolean =>
  errors.some(hasMerchantEmployeeErrors);

/** Converte um funcionário do formulário no corpo que o core recebe. */
export const merchantEmployeeToPayload = (
  employee: MerchantEmployeeFormValue
): BackofficeMerchantEmployeeRequestDto => ({
  firstName: employee.firstName.trim(),
  lastName: employee.lastName.trim(),
  email: employee.email.trim(),
  phone: employee.phone.replace(/\D/g, '') || undefined,
  document: employee.document.replace(/\D/g, '') || undefined,
  role: employee.role,
  accessEnabled: employee.accessEnabled,
  // OWNER é super-usuário: mandar perfil aqui seria guardar uma escolha que o
  // core ignora e que a próxima tela mostraria como se valesse.
  accessProfileId: employee.role === 'OWNER' ? null : employee.accessProfileId || null,
});

/** Descarta as linhas em branco e devolve a equipe que vai junto na criação da loja. */
export const merchantTeamToPayload = (
  team: MerchantEmployeeFormValue[]
): BackofficeMerchantEmployeeRequestDto[] =>
  team.filter((employee) => !isBlankMerchantEmployee(employee)).map(merchantEmployeeToPayload);

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
  line-height: 1.5;
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid ${(p) => p.theme.warning}55;
  border-left: 3px solid ${(p) => p.theme.warning};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.warning}12;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => p.theme.warning};
  }
`;

const Card = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MemberCard = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => p.theme.textMuted};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Muted = styled.span`
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

/** Texto padrão para a loja que ainda não tem nenhum perfil de acesso. */
export const NO_ACCESS_PROFILES_HINT =
  'Esta loja ainda não tem perfil de acesso - quem cria os perfis é o próprio lojista, no painel dele. ' +
  'Enquanto não houver nenhum, só dá para cadastrar o proprietário: ele é super-usuário da loja e não depende de perfil.';

export interface MerchantEmployeeFieldsProps {
  value: MerchantEmployeeFormValue;
  onChange: (employee: MerchantEmployeeFormValue) => void;
  /** Perfis da loja. Lista vazia desabilita o cargo "Funcionário". */
  profiles: BackofficeMerchantAccessProfileOptionDto[];
  profilesLoading?: boolean;
  errors?: MerchantEmployeeFieldErrors;
  disabled?: boolean;
  /** Sobrescreve o texto mostrado quando a loja não tem perfil de acesso. */
  noProfilesHint?: React.ReactNode;
  /** Desliga o aviso de "loja sem perfil" - quem lista já o mostra uma vez só. */
  hideNoProfilesNotice?: boolean;
  /** Prefixo dos `aria-label`, para distinguir as linhas de uma lista. */
  fieldPrefix?: string;
}

/**
 * Bloco de campos de UM funcionário, compartilhado pelo passo Equipe do wizard
 * e pelo formulário da ficha do lojista - as duas telas cobram exatamente as
 * mesmas regras, então não podem ter dois formulários diferentes.
 */
export const MerchantEmployeeFields: React.FC<MerchantEmployeeFieldsProps> = ({
  value,
  onChange,
  profiles,
  profilesLoading,
  errors = {},
  disabled,
  noProfilesHint,
  hideNoProfilesNotice,
  fieldPrefix,
}) => {
  const hasProfiles = profiles.length > 0;
  const label = (text: string) => (fieldPrefix ? `${fieldPrefix} - ${text}` : text);

  const patch = (changes: Partial<MerchantEmployeeFormValue>) => onChange({ ...value, ...changes });

  /**
   * Trocar para proprietário limpa o perfil escolhido. Guardar o id "por via
   * das dúvidas" seria guardar uma escolha que o core descarta - e que
   * reapareceria selecionada na próxima abertura, como se estivesse valendo.
   */
  const changeRole = (role: MerchantRole) =>
    patch({ role, accessProfileId: role === 'OWNER' ? '' : value.accessProfileId });

  return (
    <Fields>
      {!hasProfiles && !profilesLoading && !hideNoProfilesNotice && (
        <Notice>
          <Info size={15} />
          <span>{noProfilesHint ?? NO_ACCESS_PROFILES_HINT}</span>
        </Notice>
      )}
      <Grid2>
        <TextInput
          label="Nome *"
          aria-label={label('Nome')}
          disabled={disabled}
          value={value.firstName}
          error={errors.firstName}
          onChange={(e) => patch({ firstName: e.target.value })}
        />
        <TextInput
          label="Sobrenome *"
          aria-label={label('Sobrenome')}
          disabled={disabled}
          value={value.lastName}
          error={errors.lastName}
          onChange={(e) => patch({ lastName: e.target.value })}
        />
      </Grid2>
      <Grid2>
        <TextInput
          label="E-mail *"
          type="email"
          aria-label={label('E-mail')}
          disabled={disabled}
          value={value.email}
          error={errors.email}
          onChange={(e) => patch({ email: e.target.value })}
        />
        <PhoneInput
          label="Telefone"
          disabled={disabled}
          value={value.phone}
          error={errors.phone}
          onChange={(phone) => patch({ phone })}
        />
      </Grid2>
      <Grid2>
        <DocumentInput
          label="CPF"
          documentType="CPF"
          placeholder="000.000.000-00"
          disabled={disabled}
          value={value.document}
          error={errors.document}
          onChange={(document) => patch({ document })}
        />
        <Select
          label="Cargo *"
          aria-label={label('Cargo')}
          disabled={disabled}
          value={value.role}
          onChange={(e) => changeRole(e.target.value as MerchantRole)}
          options={[
            { value: 'OWNER', label: MERCHANT_ROLE_LABELS.OWNER },
            {
              value: 'STAFF',
              // Sem perfil na loja, STAFF só levaria o operador ao erro
              // ACCESS_PROFILE_REQUIRED. Desabilitado e explicado no aviso acima.
              label: hasProfiles
                ? MERCHANT_ROLE_LABELS.STAFF
                : `${MERCHANT_ROLE_LABELS.STAFF} (precisa de perfil de acesso)`,
              disabled: !hasProfiles,
            },
          ]}
        />
      </Grid2>
      <Select
        label="Perfil de acesso"
        aria-label={label('Perfil de acesso')}
        disabled={disabled || value.role === 'OWNER' || !hasProfiles}
        value={value.accessProfileId}
        error={errors.accessProfileId}
        onChange={(e) => patch({ accessProfileId: e.target.value })}
        options={[
          {
            value: '',
            label: profilesLoading
              ? 'Carregando perfis...'
              : hasProfiles
              ? 'Selecione o perfil'
              : 'Nenhum perfil cadastrado',
          },
          ...profiles.map((profile) => ({ value: profile.id, label: profile.name })),
        ]}
      />
      <ToggleField>
        <Switch
          checked={value.accessEnabled}
          disabled={disabled}
          onChange={(accessEnabled) => patch({ accessEnabled })}
        />
        Acesso ao sistema ativo
      </ToggleField>
      <Hint>
        {value.role === 'OWNER'
          ? 'Proprietário tem acesso total à loja - não depende de perfil de acesso.'
          : 'As permissões do funcionário são as do perfil de acesso escolhido.'}{' '}
        A senha temporária é gerada e enviada por e-mail; a troca é obrigatória no primeiro acesso.
      </Hint>
    </Fields>
  );
};

export interface MerchantTeamInputProps {
  /** Lista em edição. A ordem é só a de digitação - o core não a usa. */
  value: MerchantEmployeeFormValue[];
  onChange: (team: MerchantEmployeeFormValue[]) => void;
  profiles: BackofficeMerchantAccessProfileOptionDto[];
  profilesLoading?: boolean;
  disabled?: boolean;
  noProfilesHint?: React.ReactNode;
  /**
   * Marca a PRIMEIRA linha como a pessoa principal da lista - no cadastro de
   * lojista, o responsável pela loja.
   *
   * Um rótulo só, porque "quem é o responsável" precisa ser uma coisa que não
   * pode entrar em estado inválido: com marcação por seleção (um rádio por
   * linha) o operador consegue salvar sem escolher ninguém, ou escolher dois.
   * Sendo a posição, a resposta existe sempre.
   *
   * A linha já foi obrigatória. Desde o card 241 ela é um convite: a equipe
   * inteira pode ficar vazia, e o que sobra do rótulo é dizer QUEM é o
   * responsável quando alguém for preenchido. Removê-la é permitido - a linha
   * de baixo assume o rótulo, e o aviso ao lado do botão diz isso, porque a
   * promoção silenciosa é que seria a armadilha.
   */
  leadLabel?: string;
  max?: number;
}

/**
 * Lista editável da equipe da loja, usada no passo Equipe do cadastro.
 *
 * A validação é calculada aqui para cada campo mostrar a própria mensagem
 * enquanto o operador digita; `validateMerchantTeam` é exportada para a tela
 * que tem o botão de salvar cobrar exatamente as mesmas regras, em vez de
 * escrever um segundo conjunto.
 */
export const MerchantTeamInput: React.FC<MerchantTeamInputProps> = ({
  value,
  onChange,
  profiles,
  profilesLoading,
  disabled,
  noProfilesHint,
  leadLabel,
  max = 20,
}) => {
  const theme = useTheme();
  const errors = useMemo(() => validateMerchantTeam(value), [value]);
  const hasProfiles = profiles.length > 0;

  const patch = (index: number, employee: MerchantEmployeeFormValue) =>
    onChange(value.map((it, i) => (i === index ? employee : it)));

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  // Sem perfil na loja, a única linha possível é a do proprietário - abrir a
  // linha já em STAFF obrigaria o operador a corrigir um campo travado.
  const add = () => onChange([...value, emptyMerchantEmployee(hasProfiles ? 'STAFF' : 'OWNER')]);

  const canAdd = !disabled && value.length < max;

  return (
    <Card>
      {/* O aviso é da lista, não da linha: repetido em cada funcionário viraria ruído. */}
      {!hasProfiles && !profilesLoading && (
        <Notice>
          <Info size={15} />
          <span>{noProfilesHint ?? NO_ACCESS_PROFILES_HINT}</span>
        </Notice>
      )}
      {value.length === 0 && (
        <Muted>Nenhum funcionário - a equipe pode ser montada depois, na ficha do lojista.</Muted>
      )}

      {value.map((employee, index) => {
        const isLead = index === 0 && !!leadLabel;
        const rowLabel = leadLabel && index === 0 ? leadLabel : `Funcionário ${index + 1}`;
        return (
          <MemberCard key={index}>
            <MemberHeader>
              <span>{rowLabel}</span>
              <ActionButton
                type="button"
                // Remover a linha do responsável promove a de baixo. Dizer isso
                // no título é o mínimo: a promoção silenciosa é que seria a
                // armadilha, e travar o botão obrigaria o operador a deixar uma
                // linha pela metade só para conseguir salvar.
                title={
                  isLead && value.length > 1
                    ? `Remover - ${leadLabel} passa a ser o próximo da lista`
                    : 'Remover funcionário'
                }
                color={theme.error}
                disabled={disabled}
                onClick={() => remove(index)}
              >
                <Trash2 size={15} />
              </ActionButton>
            </MemberHeader>
            <MerchantEmployeeFields
              value={employee}
              onChange={(next) => patch(index, next)}
              profiles={profiles}
              profilesLoading={profilesLoading}
              errors={errors[index]}
              disabled={disabled}
              hideNoProfilesNotice
              fieldPrefix={rowLabel}
            />
          </MemberCard>
        );
      })}

      <Footer>
        <Button type="button" variant="outline" disabled={!canAdd} onClick={add}>
          <Plus size={16} /> Adicionar funcionário
        </Button>
        <Muted>
          {value.length}/{max} funcionários
        </Muted>
      </Footer>
    </Card>
  );
};

export default MerchantTeamInput;
